import React, { useState } from 'react';
import { ApiClient } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { useLayout } from '../../context/LayoutContext';
import { scrapeImportRunner, scrapeDiscoveryRunner, SCRAPER_STORAGE_KEY, type RunnerHandlers, type DiscoveryHandlers, type DiscoveryCheckpoint } from './scrapeImportRunner';
import { 
  Globe, 
  ArrowRight, 
  RefreshCw, 
  CheckCircle2, 
  Paperclip, 
  Layers, 
  Sparkles, 
  Download,
  Building2 
} from 'lucide-react';

const loadSavedState = () => {
  try {
    const raw = sessionStorage.getItem(SCRAPER_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const WebScraperView: React.FC = () => {
  const { tenant, role } = useAuth();
  const isSuper = role === 'superadmin' || role === 'tenant_admin';
  const { setActiveCategory, setActiveModule } = useLayout();

  const saved = React.useMemo(() => loadSavedState(), []);

  const [step, setStep] = useState<1 | 2 | 3>(saved?.step || 1);
  const [sourceUrlsText, setSourceUrlsText] = useState(saved?.sourceUrlsText || 'https://onkormanyzat.hu/kozerdeku-adatok');
  const [isDiscovering, setIsDiscovering] = useState(scrapeDiscoveryRunner.isRunning());
  // félbemaradt felfedezés ellenőrzőpontja (hiba / leállás után innen folytatható)
  const [discoveryCp, setDiscoveryCp] = useState<DiscoveryCheckpoint | null>(saved?.discovery || null);
  const [discoverProgress, setDiscoverProgress] = useState(saved?.discoverProgress || 0);
  const [logs, setLogs] = useState<string[]>(saved?.logs || []);
  const [plan, setPlan] = useState<any>(saved?.plan || null);

  // Import Execution
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(saved?.importProgress || { done: 0, total: 0 });
  const [pausedImportId, setPausedImportId] = useState<number | null>(saved?.pausedImportId || null);
  const [importCompleted, setImportCompleted] = useState(saved?.importCompleted || false);
  // az éppen futó lépés óta eltelt idő (mp) – hogy látszódjon, hogy a folyamat él
  const [stepWaitSec, setStepWaitSec] = useState(0);

  // Munkamenet automatikus perzisztenciája elnavigálás és visszatérés védelmére
  React.useEffect(() => {
    try {
      const stateToSave = {
        step,
        sourceUrlsText,
        logs: logs.slice(-200),
        plan,
        discoverProgress,
        importProgress,
        pausedImportId,
        importCompleted,
        discoveryRunning: isDiscovering,
      };
      let prev: any = {};
      try { prev = JSON.parse(sessionStorage.getItem(SCRAPER_STORAGE_KEY) || '{}'); } catch { prev = {}; }
      sessionStorage.setItem(SCRAPER_STORAGE_KEY, JSON.stringify({ ...prev, ...stateToSave }));
    } catch {
      // ignore quota limits
    }
  }, [step, sourceUrlsText, logs, plan, discoverProgress, importProgress, pausedImportId, importCompleted, isDiscovering]);

  const resetWizard = () => {
    if (confirm('Biztosan új scraper folyamatot indítasz? A jelenlegi mentett felderítés és előnézet törlődik.')) {
      try {
        sessionStorage.removeItem(SCRAPER_STORAGE_KEY);
      } catch {}
      scrapeDiscoveryRunner.stop();
      setDiscoveryCp(null);
      setStep(1);
      setPlan(null);
      setLogs([]);
      setDiscoverProgress(0);
      setImportProgress({ done: 0, total: 0 });
      setPausedImportId(null);
      setImportCompleted(false);
      scrapeImportRunner.stop();
    }
  };

  // ── Melléklet-letöltési időkorlát (superadmin állítja) ──
  const [fileTimeoutSec, setFileTimeoutSec] = useState(90);
  const [timeoutDraft, setTimeoutDraft] = useState('90');
  const [timeoutSaved, setTimeoutSaved] = useState('');
  React.useEffect(() => {
    ApiClient.request<{ file_timeout_sec: number }>('/admin/scrape/settings').then((r: any) => {
      const v = Number(r?.data?.file_timeout_sec);
      if (r?.ok && v) { setFileTimeoutSec(v); setTimeoutDraft(String(v)); }
    }).catch(() => {});
  }, []);
  const saveFileTimeout = async () => {
    const v = Math.min(300, Math.max(10, Math.round(Number(timeoutDraft) || 90)));
    const r: any = await ApiClient.request('/admin/scrape/settings', { method: 'POST', body: JSON.stringify({ file_timeout_sec: v }) });
    if (r?.ok) {
      setFileTimeoutSec(r.data?.file_timeout_sec ?? v);
      setTimeoutDraft(String(r.data?.file_timeout_sec ?? v));
      setTimeoutSaved('✓ Mentve – a következő lépéstől érvényes');
      window.setTimeout(() => setTimeoutSaved(''), 4000);
    } else {
      setTimeoutSaved(r?.error || 'Nem sikerült menteni');
    }
  };

  // ── Rácsatlakozás a háttérben futó átemelésre (menüváltás / oldalfrissítés után is) ──
  React.useEffect(() => {
    const h: RunnerHandlers = {
      onLog: (m) => setLogs((prev) => [...prev, m]),
      onProgress: (p) => setImportProgress(p),
      onWait: (sec) => setStepWaitSec(sec),
      onRunning: (r) => setIsImporting(r),
      onCompleted: () => { setImportCompleted(true); setPausedImportId(null); },
      onPaused: (id) => setPausedImportId(id),
    };
    scrapeImportRunner.attach(h);
    const readCp = () => { try { return JSON.parse(sessionStorage.getItem(SCRAPER_STORAGE_KEY) || '{}')?.discovery || null; } catch { return null; } };
    const dh: DiscoveryHandlers = {
      onLog: (m) => setLogs((prev) => [...prev, m]),
      onProgress: (p) => setDiscoverProgress(p),
      onRunning: (r) => { setIsDiscovering(r); if (!r) setDiscoveryCp(readCp()); },
      onDone: (p) => { setPlan(p); setStep(2); setDiscoveryCp(null); },
      onFailed: (msg) => alert(msg || 'Felfedezési hiba'),
    };
    scrapeDiscoveryRunner.attach(dh);
    // teljes oldalfrissítés után: a félbemaradt felfedezés folytatása az utolsó ellenőrzőponttól
    const cp: DiscoveryCheckpoint | null = saved?.discovery || null;
    if (cp && (saved?.step || 1) === 1 && saved?.discoveryRunning && !scrapeDiscoveryRunner.isRunning()) {
      scrapeDiscoveryRunner.run(cp.sourceUrls || [], cp);
    }
    // teljes oldalfrissítés után: a félbemaradt import automatikus folytatása
    const activeId = Number(saved?.activeImportId) || null;
    if (activeId && saved?.step === 3 && !saved?.importCompleted && !scrapeImportRunner.isRunning()) {
      setLogs((prev) => [...prev, `🔄 Folytatás a megszakadt ponttól (import #${activeId})...`]);
      scrapeImportRunner.run(activeId);
    }
    return () => { scrapeImportRunner.detach(h); scrapeDiscoveryRunner.detach(dh); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const appendLog = (msg: string) => {
    setLogs((prev) => [...prev, msg]);
  };

  const startDiscovery = async () => {
    const urls = sourceUrlsText
      .split('\n')
      .map((s: string) => s.trim())
      .filter(Boolean);

    if (urls.length === 0) {
      alert('Kérjük adjon meg legalább egy forrás weboldal URL-t!');
      return;
    }
    if (discoveryCp && !confirm('Van félbemaradt felfedezés. Biztosan elölről kezded? (A „Folytatás” gombbal ott folytathatod, ahol abbamaradt.)')) return;

    setLogs([]);
    setDiscoveryCp(null);
    // a futtató a képernyőtől függetlenül halad; menüváltás után visszatérve rácsatlakozunk
    await scrapeDiscoveryRunner.run(urls);
  };

  const resumeDiscovery = async () => {
    if (!discoveryCp) return;
    await scrapeDiscoveryRunner.run(discoveryCp.sourceUrls || [], discoveryCp);
  };

  const startImport = async () => {
    if (!plan || !plan.pages) return;

    setIsImporting(true);
    setPausedImportId(null);
    setStep(3);
    appendLog('🚀 Adatok és csatolt dokumentumok átemelése a tárolóba...');

    try {
      const startRes = await ApiClient.scrapeImportStart(plan, tenant?.id);
      if (!startRes.ok || !startRes.import_id) {
        alert(startRes.error || 'Nem sikerült elindítani az importálást');
        setIsImporting(false);
        return;
      }

      const importId = startRes.import_id;
      const total = startRes.progress_total || 1;
      setImportProgress({ done: 0, total });

      setIsImporting(false);
      await scrapeImportRunner.run(importId); // a futtató a képernyőtől függetlenül fut tovább
    } catch (e: any) {
      appendLog(`❌ Hiba: ${e.message}`);
      setIsImporting(false);
    }
  };

  const resumeImport = async () => {
    if (!pausedImportId) return;
    appendLog(`🔄 Folytatás a megszakadt ponttól (import #${pausedImportId})...`);
    const id = pausedImportId;
    setPausedImportId(null);
    await scrapeImportRunner.run(id);
  };

  const totalRecords = plan?.pages?.reduce((acc: number, p: any) => acc + (p.records?.length || 0), 0) || 0;
  const totalAttachments = plan?.pages?.reduce(
    (acc: number, p: any) => acc + p.records?.reduce((rAcc: number, r: any) => rAcc + (r.attachments?.length || 0), 0),
    0
  ) || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-5 border-b border-slate-200 dark:border-slate-800">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900 dark:text-white flex items-center gap-2.5">
            <Globe className="w-7 h-7 text-indigo-600 dark:text-indigo-400" />
            <span>Weboldal Átemelő & Dokumentum Átvétel (Scraper)</span>
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Meglévő közzétételi weboldalak automatizált feltérképezése, adatok és csatolt dokumentumok átemelése
          </p>
        </div>

        {(step > 1 || plan || logs.length > 0) && (
          <div className="flex items-center gap-3">
            <span className="text-xs text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 px-2.5 py-1 rounded-lg flex items-center gap-1.5 font-medium">
              <CheckCircle2 className="w-3.5 h-3.5" /> Munkamenet megőrizve
            </span>
            <button
              onClick={resetWizard}
              className="text-xs px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 transition cursor-pointer font-medium"
            >
              Új folyamat indítása
            </button>
          </div>
        )}
      </div>

      {/* 3-Step Wizard Indicator */}
      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        <div className={`p-3.5 rounded-xl border transition ${
          step === 1
            ? 'bg-indigo-50 border-indigo-200 text-indigo-900 dark:bg-indigo-950/40 dark:border-indigo-700 dark:text-white font-bold'
            : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-500'
        }`}>
          <div className="text-[10px] uppercase tracking-wider opacity-75">1. Lépés</div>
          <div className="text-xs sm:text-sm truncate">Weboldal URL Megadása</div>
        </div>

        <div className={`p-3.5 rounded-xl border transition ${
          step === 2
            ? 'bg-indigo-50 border-indigo-200 text-indigo-900 dark:bg-indigo-950/40 dark:border-indigo-700 dark:text-white font-bold'
            : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-500'
        }`}>
          <div className="text-[10px] uppercase tracking-wider opacity-75">2. Lépés</div>
          <div className="text-xs sm:text-sm truncate">Felfedezés & Előnézet</div>
        </div>

        <div className={`p-3.5 rounded-xl border transition ${
          step === 3
            ? 'bg-indigo-50 border-indigo-200 text-indigo-900 dark:bg-indigo-950/40 dark:border-indigo-700 dark:text-white font-bold'
            : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-500'
        }`}>
          <div className="text-[10px] uppercase tracking-wider opacity-75">3. Lépés</div>
          <div className="text-xs sm:text-sm truncate">Átemelés & Fájlletöltés</div>
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          STEP 1: FORRÁS URL-EK MEGADÁSA
      ───────────────────────────────────────────────────────────── */}
      {step === 1 && (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-xs space-y-6">
          <div>
            <label className="block text-sm font-bold text-slate-800 dark:text-slate-200 mb-1.5">
              Forrás Weboldal Címe (Közérdekű adatok / Üvegzseb oldal)
            </label>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
              Adja meg az önkormányzat vagy intézmény jelenlegi közzétételi oldalának linkjét. A rendszer automatikusan felderíti az aloldalakat, táblázatokat és a csatolt PDF/DOCX dokumentumokat.
            </p>
            <textarea
              rows={4}
              value={sourceUrlsText}
              onChange={(e) => setSourceUrlsText(e.target.value)}
              placeholder="https://pelda-onkormanyzat.hu/kozerdeku-adatok&#10;https://pelda-onkormanyzat.hu/uvegzseb"
              className="w-full p-3.5 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-slate-100 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-slate-100 dark:border-slate-800">
            <div className="text-xs text-slate-500 flex items-center gap-1.5">
              <Sparkles className="w-4 h-4 text-amber-500" />
              <span>Intelligens Infotv. pontpárosítás bekapcsolva</span>
            </div>

            <button
              onClick={startDiscovery}
              disabled={isDiscovering}
              className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 hover:bg-indigo-500 active:bg-indigo-700 text-white font-medium rounded-xl text-sm shadow-md transition cursor-pointer disabled:opacity-50"
            >
              {isDiscovering ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Felfedezés folyamatban ({discoverProgress}%)...</span>
                </>
              ) : (
                <>
                  <span>Felfedezés Indítása</span>
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>

          {/* Live Progress and Logs during Discovery */}
          {!isDiscovering && discoveryCp && (
            <div className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 text-xs text-amber-900 dark:text-amber-200">
              <span>
                Félbemaradt felfedezés: {discoveryCp.visited?.length || 0} oldal kész, {discoveryCp.queue?.length || 0} van hátra.
              </span>
              <button
                onClick={resumeDiscovery}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-500 text-white font-medium cursor-pointer"
              >
                <RefreshCw className="w-3.5 h-3.5" /> Folytatás onnan, ahol abbamaradt
              </button>
            </div>
          )}

          {(isDiscovering || (discoveryCp && logs.length > 0)) && (
            <div className="space-y-2 pt-4">
              <div className="w-full bg-slate-200 dark:bg-slate-800 h-2 rounded-full overflow-hidden">
                <div
                  className="bg-indigo-600 h-full rounded-full transition-all duration-300"
                  style={{ width: `${discoverProgress}%` }}
                />
              </div>
              <div className="bg-slate-950 text-slate-300 font-mono text-xs p-3 rounded-xl max-h-48 overflow-y-auto space-y-1">
                {logs.map((l, idx) => (
                  <div key={idx}>{l}</div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          STEP 2: FELFEDEZETT ADATOK ELŐNÉZETE & MAPPING
      ───────────────────────────────────────────────────────────── */}
      {step === 2 && plan && (
        <div className="space-y-6">
          {/* Summary KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 shadow-xs">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Feldolgozott Oldalak</span>
              <div className="text-3xl font-extrabold text-slate-900 dark:text-white mt-2">
                {plan.pages?.length || 0}
              </div>
            </div>

            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 shadow-xs">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Talált Rekordok</span>
              <div className="text-3xl font-extrabold text-indigo-600 dark:text-indigo-400 mt-2">
                {totalRecords}
              </div>
            </div>

            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 shadow-xs">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Csatolt Fájlok</span>
              <div className="text-3xl font-extrabold text-emerald-600 dark:text-emerald-400 mt-2">
                {totalAttachments}
              </div>
            </div>

            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 shadow-xs">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Kapcsolt Intézmények</span>
              <div className="text-3xl font-extrabold text-amber-600 dark:text-amber-400 mt-2">
                {plan.linkedEntities?.length || 0}
              </div>
            </div>
          </div>

          {/* Felismert Kapcsolt Szervek & Intézmények */}
          {plan.linkedEntities && plan.linkedEntities.length > 0 && (
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-xs space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <h3 className="text-base font-bold text-slate-900 dark:text-white flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                  <span>Felismert Kapcsolt Szervek, Intézmények & Cégek ({plan.linkedEntities.length} db)</span>
                </h3>
                <span className="text-xs text-emerald-600 dark:text-emerald-400 font-semibold flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 shrink-0" />
                  <span>Automatikusan bekerül a jegyzői nyilvántartásba</span>
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {plan.linkedEntities.map((ent: any, eIdx: number) => (
                  <div key={eIdx} className="p-3 bg-slate-50 dark:bg-slate-950/60 border border-slate-200 dark:border-slate-800 rounded-xl space-y-1">
                    <div className="flex items-start justify-between gap-2">
                      <span className="text-xs font-bold text-slate-900 dark:text-white line-clamp-1" title={ent.name}>{ent.name}</span>
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 shrink-0">
                        {ent.entity_type === 'hivatal' ? 'Hivatal' :
                         ent.entity_type === 'onkormanyzat' ? 'Önkormányzat' :
                         ent.entity_type === 'intezmeny' ? 'Intézmény' :
                         ent.entity_type === 'gazdalkodo_szervezet' ? 'Cég / Kft.' :
                         ent.entity_type === 'kozalapitvany' ? 'Alapítvány' : 'Egyéb'}
                      </span>
                    </div>
                    {ent.short_name && ent.short_name !== ent.name && (
                      <div className="text-[11px] text-slate-500">Rövid név: {ent.short_name}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Plan Table */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-xs space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <Layers className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                <span>Átemelésre Váró Közzétételi Oldalak és Rekordok</span>
              </h3>
              <span className="text-xs text-slate-500">Cél szervezet: {tenant?.name || 'Aktuális szervezet'}</span>
            </div>

            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              {plan.pages?.map((p: any, idx: number) => {
                const filesCount = p.records?.reduce((acc: number, r: any) => acc + (r.attachments?.length || 0), 0) || 0;

                return (
                  <div key={idx} className="py-3 flex items-start justify-between gap-4">
                    <div>
                      <div className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
                        <span>{p.title}</span>
                        {p.slug && <span className="text-xs font-mono text-slate-400">({p.slug})</span>}
                      </div>
                      <div className="text-xs text-slate-500 mt-1 flex items-center gap-3">
                        <span>{p.records?.length || 0} tétel</span>
                        {filesCount > 0 && (
                          <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-medium">
                            <Paperclip className="w-3.5 h-3.5" />
                            <span>{filesCount} csatolt dokumentum</span>
                          </span>
                        )}
                      </div>
                    </div>

                    <span className="px-2.5 py-1 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 text-xs font-medium rounded-lg">
                      Megfelelőségi ponthoz rendelve
                    </span>
                  </div>
                );
              })}
            </div>

            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600 dark:text-slate-400">
              <label htmlFor="scrape-file-timeout" className="font-medium">Letöltési időkorlát fájlonként:</label>
              {isSuper ? (
                <>
                  <input
                    id="scrape-file-timeout"
                    type="number"
                    min={10}
                    max={300}
                    value={timeoutDraft}
                    onChange={(e) => setTimeoutDraft(e.target.value)}
                    className="w-20 px-2 py-1 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-slate-900 dark:text-white"
                  />
                  <span>mp</span>
                  <button
                    type="button"
                    onClick={saveFileTimeout}
                    disabled={String(fileTimeoutSec) === timeoutDraft}
                    className="px-2.5 py-1 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white font-medium cursor-pointer"
                  >
                    Mentés
                  </button>
                  <span className="text-slate-400">(10–300 mp; ha egy fájl ennyi idő alatt nem töltődik le, kihagyja és jelzi)</span>
                  {timeoutSaved && <span className="text-emerald-600 dark:text-emerald-400">{timeoutSaved}</span>}
                </>
              ) : (
                <span id="scrape-file-timeout">{fileTimeoutSec} mp (a superadmin állítja)</span>
              )}
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-slate-100 dark:border-slate-800">
              <button
                onClick={() => setStep(1)}
                className="px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-xl text-xs font-medium hover:bg-slate-200 cursor-pointer"
              >
                &larr; Vissza a forrás URL-hez
              </button>

              <button
                onClick={startImport}
                className="flex items-center gap-2 px-6 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white font-medium rounded-xl text-sm shadow-md transition cursor-pointer"
              >
                <Download className="w-4 h-4" />
                <span>Adatok és Fájlok Átemelése ({totalRecords} rekord, {totalAttachments} fájl)</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          STEP 3: IMPORT FOLYAMATBAN / KÉSZ
      ───────────────────────────────────────────────────────────── */}
      {step === 3 && (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-8 shadow-xs text-center max-w-xl mx-auto space-y-6">
          {importCompleted ? (
            <>
              <div className="w-16 h-16 rounded-2xl bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-10 h-10" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white">Sikeres Weboldal Átemelés!</h2>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  Minden közzétételi tétel és csatolt dokumentum sikeresen importálásra került.
                </p>
              </div>
              {(() => {
                const info = [...logs].reverse().find((l) => l.startsWith('ℹ️'));
                return info ? (
                  <div className="p-3 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 text-xs text-amber-900 dark:text-amber-200 text-left">
                    {info.replace(/^ℹ️\s*/, '')}
                  </div>
                ) : null;
              })()}

              <div className="pt-2 flex justify-center gap-3">
                <button
                  onClick={() => {
                    setActiveCategory('transparency_data');
                    setActiveModule('records_manager');
                  }}
                  className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white font-medium rounded-xl text-sm shadow-md transition cursor-pointer"
                >
                  Adatlapok & Rekordok Megtekintése
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="w-16 h-16 rounded-2xl bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex items-center justify-center mx-auto">
                <RefreshCw className="w-8 h-8 animate-spin" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white">Átemelés Folyamatban...</h2>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  Dokumentumok letöltése R2 tárolóba és rekordok beillesztése D1 adatbázisba
                </p>
              </div>

              <div className="w-full bg-slate-100 dark:bg-slate-800 h-2.5 rounded-full overflow-hidden">
                <div
                  className="bg-emerald-500 h-full rounded-full transition-all duration-300"
                  style={{
                    width: `${importProgress.total > 0 ? (importProgress.done / importProgress.total) * 100 : 0}%`,
                  }}
                />
              </div>

              <div className="text-xs font-mono text-slate-500">
                Feldolgozva: {importProgress.done} / {importProgress.total} elem
              </div>
              {isImporting && stepWaitSec >= 5 && (
                <div className="text-[11px] text-amber-600 dark:text-amber-400" aria-live="polite">
                  Mellékletek letöltése a forrásoldalról… {stepWaitSec} mp (fájlonként legfeljebb {fileTimeoutSec} mp, utána kihagyja)
                </div>
              )}
              <p className="text-[11px] text-slate-400">Átnavigálhatsz más menüpontra – az átemelés a háttérben folytatódik, visszatérve itt látod az állapotát.</p>

              <div className="bg-slate-950 text-slate-300 font-mono text-xs p-3 rounded-xl max-h-40 overflow-y-auto space-y-1 text-left">
                {logs.slice(-5).map((l, idx) => (
                  <div key={idx}>{l}</div>
                ))}
              </div>

              {pausedImportId && !isImporting && (
                <div className="pt-2">
                  <button
                    onClick={resumeImport}
                    className="px-6 py-2.5 bg-amber-600 hover:bg-amber-500 text-white font-medium rounded-xl text-sm shadow-md transition cursor-pointer flex items-center gap-2 mx-auto"
                  >
                    <RefreshCw className="w-4 h-4" />
                    <span>Folytatás a megszakadt ponttól ({importProgress.done} / {importProgress.total})</span>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};
